import {createTheme} from '@mui/material/styles';
import type {Shadows} from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import tokensCss from './qualia-tokens.css?raw';

// Extract a token value directly from the live @qualia/react-components tokens.css.
// No hardcoded values — if Qualia updates a token, this theme updates automatically.
function token(name: string): string {
  const match = (tokensCss as string).match(new RegExp(`${name}:\\s*([^;]+);`));
  return match?.[1]?.trim() ?? '';
}

// Qualia token values resolved at build time from the live package
const q = {
  // Brand
  brandResware: token('--brand-resware-primary'),
  brandUniversalPrimary: token('--brand-universal-primary'),
  brandUniversalSecondary: token('--brand-universal-secondary'),

  // Surfaces
  surfacePrimary: token('--surface-primary'),
  surfaceSecondary: token('--surface-secondary'),
  surfaceTertiary: token('--surface-tertiary'),
  surfaceContrast: token('--surface-contrast'),
  surfaceOverlay: token('--surface-overlay'),
  surfaceErrorPrimary: token('--surface-error-primary'),
  surfaceErrorSecondary: token('--surface-error-secondary'),
  surfaceWarningPrimary: token('--surface-caution-primary'),
  surfaceWarningSecondary: token('--surface-caution-secondary'),
  surfaceSuccessPrimary: token('--surface-success-primary'),
  surfaceSuccessSecondary: token('--surface-success-secondary'),
  surfaceInfoPrimary: token('--surface-info-primary'),
  surfaceInfoSecondary: token('--surface-info-secondary'),

  // Text
  textPrimary: token('--text-primary'),
  textSecondary: token('--text-secondary'),
  textTertiary: token('--text-tertiary'),
  textPlaceholder: token('--text-placeholder'),
  textDisabled: token('--interactive-disabled-text'),
  textError: token('--text-error-2'),
  textWarning: token('--text-caution'),
  textSuccess: token('--text-success'),
  textInfo: token('--text-info'),
  textLink: token('--text-link'),
  textContrast: token('--text-contrast'),

  // Stroke
  strokeTertiary: token('--stroke-tertiary-default'),
  strokePrimary: token('--stroke-primary-default'),
  strokePrimaryHover: token('--stroke-primary-hover'),
  strokeError: token('--stroke-error-primary'),
  strokeSuccess: token('--stroke-success-primary'),
  strokeInfo: token('--stroke-info-primary'),
  strokeCaution: token('--stroke-caution-primary'),

  // Interactive
  interactivePrimary: token('--interactive-primary-default'),
  interactivePrimaryHover: token('--interactive-primary-hover'),
  interactivePrimaryPressed: token('--interactive-primary-pressed'),
  interactiveError: token('--interactive-error-default'),
  interactiveErrorHover: token('--interactive-error-hover'),
  interactiveErrorPressed: token('--interactive-error-pressed'),
  interactiveCaution: token('--interactive-caution-default'),
  interactiveCautionHover: token('--interactive-caution-hover'),
  interactiveCautionPressed: token('--interactive-caution-pressed'),
  interactiveDisabledFill: token('--interactive-disabled-fill'),
  interactiveDisabledText: token('--interactive-disabled-text'),
  interactiveDisabledStroke: token('--interactive-disabled-stroke'),
  interactiveSecondaryStroke: token('--interactive-secondary-stroke'),

  // Typography — body
  fontFamilyBase: token('--typography-body-m-font-family'), // Lato
  letterSpacingBase: token('--typography-body-m-letter-spacing'),
  fontFamilyLarge: token('--typography-body-l-font-family'), // Inter
  fontSizeBase: token('--typography-body-m-font-size'), // 14px
  fontSizeLarge: token('--typography-body-l-font-size'), // 16px
  fontSizeSmall: token('--typography-body-s-font-size'), // 13px
  fontSizeXs: token('--typography-body-xs-font-size'), // 12px
  lineHeightBase: token('--typography-body-m-line-height'),
  lineHeightLarge: token('--typography-body-l-line-height'),
  lineHeightSmall: token('--typography-body-s-line-height'),

  // Typography — headers
  fontSizeH1: token('--typography-header-xxl-font-size'), // 32px
  lineHeightH1: token('--typography-header-xxl-line-height'),
  letterSpacingH1: token('--typography-header-xxl-letter-spacing'),
  fontWeightH1: token('--typography-header-xxl-font-weight'),

  fontSizeH2: token('--typography-header-xl-font-size'), // 28px
  fontFamilyH2: token('--typography-header-xl-font-family'), // Inter
  lineHeightH2: token('--typography-header-xl-line-height'),
  letterSpacingH2: token('--typography-header-xl-letter-spacing'),
  fontWeightH2: token('--typography-header-xl-font-weight'),

  fontSizeH3: token('--typography-header-l-font-size'), // 24px
  lineHeightH3: token('--typography-header-l-line-height'),
  fontWeightH3: token('--typography-header-l-font-weight'),

  fontSizeH4: token('--typography-header-m-font-size'), // 20px
  lineHeightH4: token('--typography-header-m-line-height'),
  fontWeightH4: token('--typography-header-m-font-weight'),

  fontSizeH5: token('--typography-header-s-font-size'), // 18px
  lineHeightH5: token('--typography-header-s-line-height'),
  fontWeightH5: token('--typography-header-s-font-weight'),

  fontSizeH6: token('--typography-header-xs-font-size'), // 18px
  lineHeightH6: token('--typography-header-xs-line-height'),
  fontWeightH6: token('--typography-header-xs-font-weight'), // 500

  // Typography — label / supplementary
  fontSizeLabel: token('--typography-label-m-font-size'), // 15px
  fontWeightLabel: token('--typography-label-m-font-weight'), // 700
  lineHeightLabel: token('--typography-label-m-line-height'),

  fontWeightNormal: token('--typography-body-m-font-weight'), // 400
  fontSizeCaption: token('--typography-supplementary-caption-font-size'), // 12px
  lineHeightCaption: token('--typography-supplementary-caption-line-height'),
  letterSpacingCaption: token(
    '--typography-supplementary-caption-letter-spacing',
  ),

  fontSizeOverline: token('--typography-label-s_uppercase-font-size'), // 10px
  fontWeightOverline: token('--typography-label-s_uppercase-font-weight'), // 600
  letterSpacingOverline: token('--typography-label-s_uppercase-letter-spacing'),

  // Spacing
  space02: token('--space-02'), // 2px
  space04: token('--space-04'), // 4px
  space08: token('--space-08'), // 8px
  space12: token('--space-12'), // 12px
  space16: token('--space-16'), // 16px
  space24: token('--space-24'), // 24px
  space32: token('--space-32'), // 32px

  // Radius
  radiusXs: token('--radius-xs'), // 4px
  radiusS: token('--radius-s'), // 8px
  radiusM: token('--radius-m'), // 12px
  radiusFull: token('--radius-full'),

  // Elevation
  elevationXs: token('--effect-elevation-xs'),
  elevationS: token('--effect-elevation-s'),
  elevationM: token('--effect-elevation-m'),
  elevationL: token('--effect-elevation-l'),
};

const px = (v: string) => parseInt(v) || 0;

export const muiTheme = createTheme({
  palette: {
    primary: {
      main: q.brandUniversalPrimary,
      contrastText: q.brandUniversalSecondary,
    },
    secondary: {
      main: q.brandResware,
      contrastText: q.brandUniversalSecondary,
    },
    // `command` is a chat.ui-specific palette alias for the Resware brand red,
    // referenced by legacy chat.ui code (CommandMenu, CommandPill, etc).
    // The augmentation in src/theme/themePrimitives.ts requires it; we mirror
    // `secondary` here so muiTheme satisfies the augmented PaletteOptions on
    // its own. Once the legacy references are migrated to `secondary`, this
    // entry can be deleted along with the augmentation.
    command: {
      main: q.brandResware,
      light: q.brandResware,
      dark: q.brandResware,
      contrastText: q.brandUniversalSecondary,
      50: q.brandResware,
      100: q.brandResware,
      200: q.brandResware,
      300: q.brandResware,
      400: q.brandResware,
      500: q.brandResware,
      600: q.brandResware,
      700: q.brandResware,
      800: q.brandResware,
      900: q.brandResware,
    },
    error: {
      main: q.interactiveError,
      light: q.surfaceErrorPrimary,
      dark: q.interactiveErrorPressed,
      contrastText: q.brandUniversalSecondary,
    },
    warning: {
      main: q.interactiveCaution,
      light: q.surfaceWarningPrimary,
      dark: q.interactiveCautionPressed,
      contrastText: q.brandUniversalSecondary,
    },
    success: {
      main: q.surfaceSuccessSecondary,
      light: q.surfaceSuccessPrimary,
      dark: q.textSuccess,
      contrastText: q.brandUniversalSecondary,
    },
    info: {
      main: q.surfaceInfoSecondary,
      light: q.surfaceInfoPrimary,
      dark: q.textInfo,
      contrastText: q.brandUniversalSecondary,
    },
    background: {
      default: q.surfacePrimary,
      paper: q.surfaceSecondary,
    },
    text: {
      primary: q.textPrimary,
      secondary: q.textSecondary,
      disabled: q.textDisabled,
    },
    divider: q.strokeTertiary,
    action: {
      hover: q.surfaceTertiary,
      selected: q.surfaceTertiary,
      disabled: q.interactiveDisabledText,
      disabledBackground: q.interactiveDisabledFill,
    },
  },

  typography: {
    fontFamily: `${q.fontFamilyBase}, Helvetica Neue, Arial, Helvetica, sans-serif`,
    fontSize: px(q.fontSizeBase),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeH1,
      fontWeight: px(q.fontWeightH1),
      lineHeight: q.lineHeightH1,
      letterSpacing: q.letterSpacingH1,
    },
    h2: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeH2,
      fontWeight: px(q.fontWeightH2),
      lineHeight: q.lineHeightH2,
      letterSpacing: q.letterSpacingH2,
    },
    h3: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeH3,
      fontWeight: px(q.fontWeightH3),
      lineHeight: q.lineHeightH3,
    },
    h4: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeH4,
      fontWeight: px(q.fontWeightH4),
      lineHeight: q.lineHeightH4,
    },
    h5: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeH5,
      fontWeight: px(q.fontWeightH5),
      lineHeight: q.lineHeightH5,
    },
    h6: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeH6,
      fontWeight: px(q.fontWeightH6),
      lineHeight: q.lineHeightH6,
    },
    subtitle1: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeLarge,
      lineHeight: q.lineHeightLarge,
    },
    subtitle2: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeLabel,
      fontWeight: px(q.fontWeightLabel),
      lineHeight: q.lineHeightLabel,
    },
    body1: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeBase,
      lineHeight: q.lineHeightBase,
    },
    body2: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeSmall,
      lineHeight: q.lineHeightSmall,
    },
    caption: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeCaption,
      lineHeight: q.lineHeightCaption,
      letterSpacing: q.letterSpacingCaption,
    },
    overline: {
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeOverline,
      fontWeight: px(q.fontWeightOverline),
      letterSpacing: q.letterSpacingOverline,
      textTransform: 'uppercase' as const,
    },
    button: {
      textTransform: 'none' as const,
      fontFamily: q.fontFamilyBase,
      fontSize: q.fontSizeBase,
      fontWeight: 700,
    },
  },

  spacing: 4,

  shape: {
    borderRadius: px(q.radiusXs),
  },

  shadows: [
    'none',
    q.elevationXs,
    q.elevationXs,
    q.elevationXs,
    q.elevationXs,
    q.elevationS,
    q.elevationS,
    q.elevationS,
    q.elevationS,
    q.elevationM,
    q.elevationM,
    q.elevationM,
    q.elevationM,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
    q.elevationL,
  ] as unknown as Shadows,

  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          height: q.space32,
          borderRadius: q.radiusXs,
          textTransform: 'none',
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeBase,
          padding: '0 var(--space-12)',
        },
        sizeSmall: {
          height: '28px',
          padding: '0 var(--space-08)',
          fontSize: q.fontSizeXs,
        },
        sizeLarge: {
          height: '36px',
          padding: '0 var(--space-16)',
        },
        outlined: {
          borderColor: q.strokePrimary,
          '&:hover': {borderColor: q.strokePrimary},
          '&.Mui-disabled': {
            borderColor: q.interactiveDisabledStroke,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          backgroundColor: q.surfacePrimary,
          // Force 32px height — !important beats MUI's size-variant injections
          '&:not(.MuiInputBase-multiline)': {height: `${q.space32} !important`},
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: q.strokePrimaryHover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: q.textPrimary,
            borderWidth: '1px',
          },
          '&.Mui-disabled': {
            backgroundColor: q.interactiveDisabledFill,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: q.interactiveDisabledStroke,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: q.textError,
          },
        },
        notchedOutline: {
          borderColor: q.strokeTertiary,
        },
        input: {
          fontSize: q.fontSizeSmall,
          letterSpacing: 'var(--resware-letter-spacing-sm)',
          color: q.textPrimary,
          // Fieldset border is absolutely positioned, so the input fills the full 32px root.
          '&:not(textarea)': {
            height: `${q.space32} !important`,
            padding: '0 10px !important',
            boxSizing: 'border-box',
          },
          // Multiline/textarea: vertical padding only
          '&textarea': {padding: '9px 10px'},
          '&::placeholder': {color: q.textTertiary, opacity: 0.6},
          '&.Mui-disabled': {
            color: q.interactiveDisabledText,
            WebkitTextFillColor: q.interactiveDisabledText,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {borderRadius: `${q.radiusXs} ${q.radiusXs} 0 0`},
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderBottom: `1px solid ${q.strokeTertiary}`,
        },
        indicator: {
          backgroundColor: q.brandResware,
          height: 1.75,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeBase,
          fontWeight: q.fontWeightNormal,
          letterSpacing: q.letterSpacingBase,
          minHeight: 40,
          height: 40,
          color: q.textSecondary,
          padding: `0 ${q.space16}`,
          '&:hover': {
            color: q.textPrimary,
          },
          '&.Mui-selected': {
            color: q.textPrimary,
            fontWeight: 700,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: q.space02,
          color: q.strokePrimary,
        },
        sizeSmall: {
          '& .MuiSvgIcon-root': {fontSize: 18},
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {gap: 0, marginLeft: 0, marginRight: 0},
        label: {
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeBase,
          letterSpacing: q.letterSpacingBase,
          color: q.textPrimary,
          marginLeft: 4,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // Root: total area including ripple space (8px h, 7px v on each side)
        root: {
          width: 44,
          height: 30,
          padding: '7px 8px',
        },
        // Thumb: 12×12 white circle with elevation, positioned 2px inside track
        thumb: {
          width: 12,
          height: 12,
          boxShadow: q.elevationS,
          color: q.surfacePrimary,
        },
        // SwitchBase: padding positions thumb 2px inside track edges
        // Track left = 8px, thumb target = 10px → padding-x = 10px
        // Track top  = 7px, thumb target =  9px → padding-y =  9px
        switchBase: {
          padding: '9px 10px',
          color: q.surfacePrimary,
          '&.Mui-checked': {
            color: q.surfacePrimary,
            transform: 'translateX(12px)',
            '& + .MuiSwitch-track': {
              backgroundColor: q.textLink,
              opacity: 1,
            },
          },
          '&.Mui-disabled + .MuiSwitch-track': {opacity: 0.4},
        },
        // Track: 28×16 pill, matches v1 exactly
        track: {
          width: 28,
          height: 16,
          borderRadius: 8,
          backgroundColor: q.strokePrimary,
          opacity: 1,
        },
        // Lock sizeSmall to our geometry — prevents MUI's variant from changing thumb/track dims
        sizeSmall: {
          width: 44,
          height: 30,
          '& .MuiSwitch-thumb': {width: 12, height: 12},
          '& .MuiSwitch-switchBase': {
            padding: '9px 10px',
            '&.Mui-checked': {transform: 'translateX(12px)'},
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${q.strokeTertiary}`,
          borderRadius: q.radiusS,
          backgroundColor: q.surfacePrimary,
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeBase,
          color: q.textPrimary,
          '--DataGrid-t-color-background-base': q.surfacePrimary,
          '--DataGrid-t-color-background-overlay': q.surfaceSecondary,
          '--DataGrid-t-color-border-base': q.strokeTertiary,
          '--DataGrid-t-color-foreground-base': q.textPrimary,
          '--DataGrid-t-color-foreground-muted': q.textTertiary,
          '--DataGrid-t-color-foreground-disabled': q.textDisabled,
          '--DataGrid-t-color-foreground-error': q.textError,
          '--DataGrid-t-color-interactive-hover': q.surfaceTertiary,
          '--DataGrid-t-radius-base': q.radiusXs,
          '--DataGrid-t-typography-font-family-base': q.fontFamilyBase,
          '--DataGrid-rowBorderColor': q.strokeTertiary,
          '--unstable_DataGrid-headWeight': q.fontWeightLabel,
        },
        columnHeader: {
          backgroundColor: q.surfaceSecondary,
          color: q.textSecondary,
          fontSize: q.fontSizeBase,
          fontFamily: q.fontFamilyBase,
        },
        row: {
          '&:hover': {backgroundColor: q.surfaceTertiary},
          '&.Mui-selected': {backgroundColor: q.surfaceTertiary},
        },
        cell: {
          borderColor: q.strokeTertiary,
          color: q.textPrimary,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: q.textPrimary,
          color: q.surfacePrimary,
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeXs,
          fontWeight: q.fontWeightNormal,
          letterSpacing: '-0.1px',
          padding: '5px var(--space-08)',
          borderRadius: q.radiusXs,
          whiteSpace: 'nowrap',
        },
        arrow: {
          color: q.textPrimary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: q.radiusFull,
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeSmall,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          height: `${q.space32} !important`,
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          paddingLeft: '10px !important',
          paddingRight: `${q.space32} !important`,
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          minHeight: '0 !important',
          fontSize: q.fontSizeSmall,
          letterSpacing: 'var(--resware-letter-spacing-sm)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeSmall,
          letterSpacing: 'var(--resware-letter-spacing-sm)',
          color: q.textPrimary,
          '&:hover': {backgroundColor: q.surfaceTertiary},
          '&.Mui-selected': {backgroundColor: q.surfaceTertiary},
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: q.radiusXs,
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeBase,
          padding: `${q.space04} ${q.space12}`,
        },
      },
      variants: [
        {
          props: {severity: 'error', variant: 'standard'},
          style: {
            backgroundColor: q.surfaceErrorPrimary,
            color: q.textError,
            '& .MuiAlert-icon': {color: q.textError},
          },
        },
        {
          props: {severity: 'warning', variant: 'standard'},
          style: {
            backgroundColor: q.surfaceWarningPrimary,
            color: q.textWarning,
            '& .MuiAlert-icon': {color: q.textWarning},
          },
        },
        {
          props: {severity: 'success', variant: 'standard'},
          style: {
            backgroundColor: q.surfaceSuccessPrimary,
            color: q.textSuccess,
            '& .MuiAlert-icon': {color: q.textSuccess},
          },
        },
        {
          props: {severity: 'info', variant: 'standard'},
          style: {
            backgroundColor: q.surfaceInfoPrimary,
            color: q.textInfo,
            '& .MuiAlert-icon': {color: q.textInfo},
          },
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: q.radiusS,
          boxShadow: q.elevationL,
          backgroundColor: q.surfacePrimary,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {paddingTop: q.space16, paddingBottom: q.space16},
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {borderColor: q.strokeTertiary},
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: q.strokeTertiary,
          fontFamily: q.fontFamilyBase,
          fontSize: q.fontSizeBase,
          color: q.textPrimary,
        },
        head: {
          color: q.textSecondary,
          backgroundColor: q.surfaceSecondary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: q.brandUniversalPrimary,
          color: q.brandUniversalSecondary,
          boxShadow: q.elevationS,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {color: q.textTertiary},
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {color: q.textLink},
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: q.radiusS,
          border: `1px solid ${q.strokeTertiary}`,
          backgroundColor: q.surfacePrimary,
          boxShadow: q.elevationXs,
          '&:before': {display: 'none'},
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 56,
          '&.Mui-expanded': {minHeight: 56},
        },
        content: {
          margin: `${q.space12} ${q.space16} ${q.space12} 0`,
          '&.Mui-expanded': {
            margin: `${q.space12} ${q.space16} ${q.space12} 0`,
          },
        },
        expandIconWrapper: {
          color: q.textTertiary,
        },
      },
    },
  },
});
